/**
 * Client-side export of a Helm walk-forward result into a portable Markdown
 * or HTML report — for downloading and feeding into external analysis
 * (e.g. pasting into an AI chat) without sending any data to a server.
 */

import { wfHeadline } from '../lib/analytics';

function fmtDate(s) { return s ? String(s).slice(0, 10) : '—'; }
function fmtPct(v)  { return v != null ? `${v > 0 ? '+' : ''}${v.toFixed(2)}%` : '—'; }
function fmtNum(v)  { return v != null ? v.toFixed(2) : '—'; }
function fmtMoney(v){ return v != null ? `Rs ${Math.round(v).toLocaleString('en-IN')}` : '—'; }
function symbol(t)  { return (t.symbol || '').replace('.NS', ''); }

function computeAggregates(wfRun, windows) {
  const h = wfHeadline(wfRun, windows) || {};
  const allTrades = windows.flatMap(w => w.live_trades || []);
  const wins = allTrades.filter(t => t.pnl > 0).length;
  const winRate = allTrades.length ? (wins / allTrades.length) * 100 : 0;
  const switches = windows.filter(w => w.pair_changed).length;

  return {
    initial: wfRun.initial_funds,
    final: h.final_value ?? wfRun.initial_funds,
    totalRet: h.total_return_pct ?? 0,
    maxDD: h.max_drawdown_pct ?? 0,
    allTrades,
    winRate,
    switches,
    h,
  };
}

// Headline rows shared by the Markdown and HTML renderers, so the two exports
// cannot drift apart. Risk-adjusted figures lead: the ranker selects each
// window on Sharpe, so a report headed by raw return misrepresents what the
// run was optimising for.
function headlineRows(agg) {
  const h = agg.h || {};
  const bs = h.bootstrap;
  const rows = [
    ['Final Portfolio Value', fmtMoney(agg.final)],
    ['Total Return', fmtPct(agg.totalRet)],
    [
      h.cagr_is_annualized ? 'CAGR' : `Period Return (${h.period_years?.toFixed(2) ?? '?'}y, not annualised)`,
      fmtPct(h.cagr_pct),
    ],
    ['Sharpe', fmtNum(h.sharpe_ratio)],
    ['Sortino', fmtNum(h.sortino_ratio)],
    ['Max Drawdown', `-${fmtNum(agg.maxDD)}%`],
    [
      'Exposure (time in market)',
      h.time_in_market_pct != null ? `${h.time_in_market_pct.toFixed(1)}%` : 'not recorded',
    ],
    [
      'Avg Deployed',
      h.avg_exposure_pct != null ? `${h.avg_exposure_pct.toFixed(1)}%` : 'not recorded',
    ],
    [
      'Expectancy',
      h.expectancy_r != null ? `${h.expectancy_r.toFixed(2)}R (${fmtPct(h.expectancy_pct)} / trade)` : '—',
    ],
    ['Win Rate', `${fmtNum(agg.winRate)}%`],
    ['Total Trades', String(agg.allTrades.length)],
    ['Pair Switches', String(agg.switches)],
  ];
  if (h.active_sharpe_ratio != null) {
    rows.splice(4, 0, ['Sharpe (deployed days only)', fmtNum(h.active_sharpe_ratio)]);
  }
  if (bs?.sharpe_p2_5 != null) {
    rows.push([
      'Sharpe 95% CI (bootstrap)',
      `${fmtNum(bs.sharpe_p2_5)} to ${fmtNum(bs.sharpe_p97_5)}${bs.sharpe_beats_zero === false ? ' — includes zero' : ''}`,
    ]);
  }
  return rows;
}

export function buildMarkdownReport(wfRun, windows) {
  const agg = computeAggregates(wfRun, windows);
  const a = wfRun.analytics || {};
  const lines = [];

  lines.push('# Walk-Forward Backtest Report');
  lines.push('');
  lines.push(`- **Helm ID:** ${wfRun.helm_id}`);
  lines.push(`- **WF Run ID:** ${wfRun.id}`);
  lines.push(`- **Period:** ${fmtDate(wfRun.wf_start)} → ${fmtDate(wfRun.wf_end)}`);
  lines.push(`- **Config:** ${wfRun.training_days}d training / ${wfRun.interval_days}d interval, ranked by ${wfRun.rank_by}, top_k=${wfRun.top_k}`);
  lines.push(`- **Initial Funds:** ${fmtMoney(wfRun.initial_funds)}`);
  lines.push(`- **Status:** ${wfRun.status}`);
  lines.push(`- **Report generated:** ${new Date().toISOString()}`);
  lines.push('');

  lines.push('## Aggregate Performance');
  lines.push('');
  lines.push('| Metric | Value |');
  lines.push('|---|---|');
  for (const [label, value] of headlineRows(agg)) {
    lines.push(`| ${label} | ${value} |`);
  }
  lines.push(`| Windows | ${windows.length} |`);
  lines.push('');

  if (a.monthly_returns?.length) {
    lines.push('## Monthly Returns');
    lines.push('');
    lines.push('| Year | Month | Return |');
    lines.push('|---|---|---|');
    for (const m of a.monthly_returns) {
      lines.push(`| ${m.year} | ${m.month} | ${fmtPct(m.return_pct)} |`);
    }
    lines.push('');
  }

  if (a.drawdown_stats?.n_episodes > 0) {
    const s = a.drawdown_stats;
    lines.push('## Drawdown Stats');
    lines.push('');
    lines.push(`- Episodes: ${s.n_episodes}`);
    lines.push(`- Max drawdown duration: ${s.max_drawdown_duration} days`);
    lines.push(`- Avg drawdown duration: ${s.avg_drawdown_duration} days`);
    lines.push('');
  }

  const lastSharpe = [...(a.rolling_sharpe || [])].reverse()
    .find(pt => pt.sharpe_3m != null || pt.sharpe_6m != null || pt.sharpe_12m != null);
  if (lastSharpe) {
    lines.push('## Rolling Sharpe (most recent)');
    lines.push('');
    lines.push(`- 3 month: ${fmtNum(lastSharpe.sharpe_3m)}`);
    lines.push(`- 6 month: ${fmtNum(lastSharpe.sharpe_6m)}`);
    lines.push(`- 12 month: ${fmtNum(lastSharpe.sharpe_12m)}`);
    lines.push('');
  }

  lines.push('## Windows');
  lines.push('');
  lines.push('| # | Live Period | Screener | Trade Config | Switch | Return | Sharpe | Sortino | Exposure | Trades | Final Funds |');
  lines.push('|---|---|---|---|---|---|---|---|---|---|---|');
  for (const w of windows) {
    const wm = w.live_metrics || {};
    const sw = w.pair_changed === true ? 'Changed' : w.pair_changed === false ? 'Same' : '—';
    const exp = wm.time_in_market_pct != null ? `${wm.time_in_market_pct.toFixed(0)}%` : '—';
    lines.push(`| ${w.window_num} | ${fmtDate(w.live_start)} → ${fmtDate(w.live_end)} | ${w.screener_name} | ${w.trade_config_name} | ${sw} | ${fmtPct(wm.total_return_pct)} | ${fmtNum(wm.sharpe_ratio)} | ${fmtNum(wm.sortino_ratio)} | ${exp} | ${wm.total_trades ?? '—'} | ${fmtMoney(w.final_funds)} |`);
  }
  lines.push('');

  lines.push('## Trade Log (all windows)');
  lines.push('');
  for (const w of windows) {
    const trades = w.live_trades || [];
    lines.push(`### Window ${w.window_num} — ${w.screener_name} × ${w.trade_config_name} (${fmtDate(w.live_start)} → ${fmtDate(w.live_end)})`);
    lines.push('');
    if (!trades.length) {
      lines.push('_No trades executed in this window._');
      lines.push('');
      continue;
    }
    lines.push('| Symbol | Entry | Exit | Shares | Entry ₹ | Exit ₹ | P&L | P&L % | Hold (d) | Reason |');
    lines.push('|---|---|---|---|---|---|---|---|---|---|');
    for (const t of trades) {
      lines.push(`| ${symbol(t)} | ${fmtDate(t.entry_date)} | ${fmtDate(t.exit_date)} | ${t.shares} | ${fmtNum(t.entry_price)} | ${fmtNum(t.exit_price)} | ${t.pnl?.toFixed(0)} | ${fmtPct(t.pnl_pct)} | ${t.holding_days} | ${t.exit_reason} |`);
    }
    lines.push('');
  }

  return lines.join('\n');
}

function esc(str) {
  return String(str ?? '').replace(/[&<>"']/g, c => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

export function buildHtmlReport(wfRun, windows) {
  const agg = computeAggregates(wfRun, windows);
  const a = wfRun.analytics || {};

  const windowRows = windows.map(w => {
    const wm = w.live_metrics || {};
    const sw = w.pair_changed === true ? 'Changed' : w.pair_changed === false ? 'Same' : '—';
    const retClass = wm.total_return_pct >= 0 ? 'win' : 'loss';
    return `<tr>
      <td>${w.window_num}</td>
      <td>${esc(fmtDate(w.live_start))} → ${esc(fmtDate(w.live_end))}</td>
      <td>${esc(w.screener_name)}</td>
      <td>${esc(w.trade_config_name)}</td>
      <td>${sw}</td>
      <td class="${retClass}">${fmtPct(wm.total_return_pct)}</td>
      <td>${fmtNum(wm.sharpe_ratio)}</td>
      <td>${fmtNum(wm.sortino_ratio)}</td>
      <td>${wm.time_in_market_pct != null ? `${wm.time_in_market_pct.toFixed(0)}%` : '—'}</td>
      <td>${wm.total_trades ?? '—'}</td>
      <td>${fmtMoney(w.final_funds)}</td>
    </tr>`;
  }).join('\n');

  const monthlyRows = (a.monthly_returns || []).map(m =>
    `<tr><td>${m.year}</td><td>${m.month}</td><td class="${m.return_pct >= 0 ? 'win' : 'loss'}">${fmtPct(m.return_pct)}</td></tr>`
  ).join('\n');

  const tradeSections = windows.map(w => {
    const trades = w.live_trades || [];
    const rows = trades.length
      ? trades.map(t => `<tr>
          <td>${esc(symbol(t))}</td>
          <td>${esc(fmtDate(t.entry_date))}</td>
          <td>${esc(fmtDate(t.exit_date))}</td>
          <td>${t.shares}</td>
          <td>${fmtNum(t.entry_price)}</td>
          <td>${fmtNum(t.exit_price)}</td>
          <td class="${t.pnl > 0 ? 'win' : 'loss'}">${t.pnl?.toFixed(0)}</td>
          <td class="${t.pnl_pct > 0 ? 'win' : 'loss'}">${fmtPct(t.pnl_pct)}</td>
          <td>${t.holding_days}d</td>
          <td>${esc(t.exit_reason)}</td>
        </tr>`).join('\n')
      : '<tr><td colspan="10" class="empty">No trades executed in this window.</td></tr>';

    return `<h3>Window ${w.window_num} — ${esc(w.screener_name)} × ${esc(w.trade_config_name)}
      <span class="sub">(${esc(fmtDate(w.live_start))} → ${esc(fmtDate(w.live_end))})</span></h3>
    <table>
      <thead><tr><th>Symbol</th><th>Entry</th><th>Exit</th><th>Shares</th><th>Entry ₹</th><th>Exit ₹</th><th>P&amp;L</th><th>P&amp;L %</th><th>Hold</th><th>Reason</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
  }).join('\n');

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<title>Walk-Forward Backtest Report — Helm ${wfRun.helm_id} / Run ${wfRun.id}</title>
<style>
  :root { color-scheme: light dark; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; max-width: 960px; margin: 2rem auto; padding: 0 1.5rem; line-height: 1.5; color: #1a1a1a; background: #fff; }
  h1 { font-size: 1.6rem; margin-bottom: 0.25rem; }
  h2 { font-size: 1.2rem; margin-top: 2.5rem; border-bottom: 1px solid #ddd; padding-bottom: 0.3rem; }
  h3 { font-size: 1rem; margin-top: 1.75rem; }
  h3 .sub { font-weight: 400; color: #666; font-size: 0.85rem; }
  table { width: 100%; border-collapse: collapse; margin: 0.75rem 0 1.5rem; font-size: 0.85rem; }
  th, td { text-align: left; padding: 0.45rem 0.6rem; border-bottom: 1px solid #eee; }
  th { background: #f6f6f6; font-weight: 600; }
  td.win { color: #0a7a3d; font-weight: 600; }
  td.loss { color: #c0362c; font-weight: 600; }
  td.empty { text-align: center; color: #888; font-style: italic; }
  .meta { color: #444; font-size: 0.9rem; }
  .meta div { margin: 0.15rem 0; }
  @media (prefers-color-scheme: dark) {
    body { background: #111; color: #eee; }
    h2 { border-color: #333; }
    th { background: #1c1c1c; }
    th, td { border-color: #2a2a2a; }
    .meta { color: #aaa; }
    h3 .sub { color: #888; }
  }
</style>
</head>
<body>
  <h1>Walk-Forward Backtest Report</h1>
  <div class="meta">
    <div><strong>Helm ID:</strong> ${wfRun.helm_id} &nbsp; <strong>WF Run ID:</strong> ${wfRun.id}</div>
    <div><strong>Period:</strong> ${esc(fmtDate(wfRun.wf_start))} → ${esc(fmtDate(wfRun.wf_end))}</div>
    <div><strong>Config:</strong> ${wfRun.training_days}d training / ${wfRun.interval_days}d interval, ranked by ${esc(wfRun.rank_by)}, top_k=${wfRun.top_k}</div>
    <div><strong>Initial Funds:</strong> ${fmtMoney(wfRun.initial_funds)} &nbsp; <strong>Status:</strong> ${esc(wfRun.status)}</div>
    <div><strong>Report generated:</strong> ${new Date().toISOString()}</div>
  </div>

  <h2>Aggregate Performance</h2>
  <table>
    <tbody>
      ${headlineRows(agg).map(([label, value]) =>
        `<tr><th>${esc(label)}</th><td>${esc(value)}</td></tr>`
      ).join('\n      ')}
      <tr><th>Windows</th><td>${windows.length}</td></tr>
    </tbody>
  </table>

  ${monthlyRows ? `<h2>Monthly Returns</h2>
  <table>
    <thead><tr><th>Year</th><th>Month</th><th>Return</th></tr></thead>
    <tbody>${monthlyRows}</tbody>
  </table>` : ''}

  <h2>Windows</h2>
  <table>
    <thead><tr><th>#</th><th>Live Period</th><th>Screener</th><th>Trade Config</th><th>Switch</th><th>Return</th><th>Sharpe</th><th>Sortino</th><th>Exposure</th><th>Trades</th><th>Final Funds</th></tr></thead>
    <tbody>${windowRows}</tbody>
  </table>

  <h2>Trade Log (all windows)</h2>
  ${tradeSections}
</body>
</html>`;
}

export function downloadTextFile(filename, content, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
